import React from 'react';
import PropTypes from 'prop-types';
import { Form, Field } from 'components/form';
import View from 'components/view';
import Modal from 'components/modal';
import Icon from 'components/icon';
import Button from 'components/button';
import FileReader from 'components/form/file-reader';
import SectionHeader from 'components/section-header';
import { imageUrl } from 'utils';
import { applyTheme, getStoredTheme } from 'utils/theme';
import './profile.scss';

const THEME_OPTIONS = [
  {
    value: 'light',
    label: 'Light',
    icon: { fa: 'sun-bright', faType: 'regular' },
  },
  { value: 'dark', label: 'Dark', icon: { fa: 'moon', faType: 'regular' } },
  {
    value: 'system',
    label: 'System',
    icon: { fa: 'display', faType: 'solid' },
  },
];

export default class ProfilePage extends React.PureComponent {
  static propTypes = {
    user: PropTypes.object.isRequired,
    session: PropTypes.object.isRequired,
    edited: PropTypes.bool,
    onUploadPhoto: PropTypes.func.isRequired,
    onChangeForm: PropTypes.func.isRequired,
    onSubmitProfile: PropTypes.func.isRequired,
    // Additive; defaults keep the admin render identical.
    hidePreferences: PropTypes.bool,
    themeScope: PropTypes.oneOf(['server', 'local']),
    detailsReadOnly: PropTypes.bool,
    // Replaces the default Preferences fields
    preferences: PropTypes.node,
  };

  static contextTypes = {
    onClickLogout: PropTypes.func.isRequired,
    notifyError: PropTypes.func.isRequired,
  };

  state = {
    showChangePassword: false,
  };

  onClickChangePassword = (event) => {
    event.preventDefault();
    this.setState({ showChangePassword: !this.state.showChangePassword });
  };

  onSubmitChangePassword = async (values) => {
    const { notifyError } = this.context;
    const { onSubmitProfile } = this.props;

    if (values.password !== values.confirm_password) {
      notifyError('Passwords do not match');
      return;
    }

    const success = await onSubmitProfile({ password: values.password });

    if (success) {
      this.setState({ showChangePassword: false });
    }
  };

  renderChangePassword() {
    return (
      <Form onSubmit={this.onSubmitChangePassword} autoFocus={true}>
        <Modal
          title="Change password"
          actions={[
            { label: 'Save', type: 'submit' },
            {
              label: 'Cancel',
              type: 'cancel',
              onClick: this.onClickChangePassword,
            },
          ]}
          width={500}
          cancel={false}
          onClose={this.onClickChangePassword}
        >
          <div className="row">
            <Field
              type="password"
              label="New password"
              name="password"
              required={true}
              autoComplete="new-password"
              className="span4"
            />
          </div>
          <div className="row">
            <Field
              type="password"
              label="Confirm password"
              name="confirm_password"
              rules="required, password_admin"
              autoComplete="confirm-password"
              className="span4"
            />
          </div>
        </Modal>
      </Form>
    );
  }

  render() {
    const {
      user,
      session,
      edited,
      onUploadPhoto,
      onChangeForm,
      onSubmitProfile,
      hidePreferences = false,
      themeScope = 'server',
      detailsReadOnly = false,
      preferences,
    } = this.props;

    const { onClickLogout } = this.context;
    const { showChangePassword } = this.state;

    // Super admins should not edit profile here
    const disabled = session.is_super_admin;
    const detailsDisabled = disabled || detailsReadOnly;

    return (
      <div className="profile">
        <Form onSubmit={onSubmitProfile} onChange={onChangeForm}>
          <View
            detail={true}
            smaller={true}
            headerTitle={user.name}
            headerComponent={
              <div className="with-image">
                <div className="view-header-image">
                  {user.photo && (
                    <img
                      src={imageUrl(user.photo, { width: 120, height: 120 })}
                      alt="Upload"
                    />
                  )}
                  <div
                    className={`profile-photo-upload ${
                      user.photo ? '' : 'empty'
                    }`}
                  >
                    <FileReader
                      accept="image/*"
                      onFiles={onUploadPhoto}
                      multiple={false}
                    >
                      <span className="profile-photo-upload-label">
                        {user.photo ? <Icon fa="edit" /> : <Icon fa="image" />}
                      </span>
                    </FileReader>
                  </div>
                </div>
                <h3 className="view-header-name">{user.name}</h3>
                <div className="view-header-note">{user.username}</div>
              </div>
            }
            headerActions={[
              {
                label: 'Save profile',
                type: edited ? 'default' : 'secondary',
                disabled: !edited,
                submit: true,
              },
            ]}
            footerActions={[
              {
                label: 'Log out',
                onClick: onClickLogout,
                type: 'danger inverse',
              },
            ]}
          >
            <fieldset>
              <div className="view-body-section-group">
                <div className="view-body-section">
                  <div className="view-body-subheader">
                    <SectionHeader
                      className="view-body-subtitle"
                      title="Your details"
                    />
                  </div>
                  <div className="view-section-content">
                    <div className="row">
                      <Field
                        type="text"
                        name="name"
                        label="Your name"
                        required={true}
                        defaultValue={user.name}
                        disabled={disabled}
                        className="span2"
                      />
                      <Field
                        type="text"
                        name="username"
                        label="User name"
                        required={true}
                        defaultValue={user.username}
                        disabled={detailsDisabled}
                        className="span2"
                      />
                    </div>
                    <div className="row">
                      <Button
                        onClick={this.onClickChangePassword}
                        type="secondary"
                        size="sm"
                        disabled={disabled}
                      >
                        Change password
                      </Button>
                    </div>
                  </div>
                </div>
                {preferences && (
                  <div className="view-body-section">
                    <div className="view-body-subheader">
                      <SectionHeader
                        className="view-body-subtitle"
                        title="Preferences"
                      />
                    </div>
                    <div className="view-section-content">{preferences}</div>
                  </div>
                )}
                {!preferences && !hidePreferences && (
                  <div className="view-body-section">
                    <div className="view-body-subheader">
                      <SectionHeader
                        className="view-body-subtitle"
                        title="Preferences"
                      />
                    </div>
                    <div className="view-section-content">
                      <div className="row">
                        <Field
                          type="checkbox"
                          name="notify"
                          label="Notify me when an order is placed"
                          disabled={disabled}
                          defaultChecked={user.notify}
                        />
                      </div>
                      <div className="row">
                        <Field
                          type="checkbox"
                          name="devtools"
                          label="Enable developer tools"
                          help="Turn on a few useful shortcuts for developers"
                          disabled={disabled}
                          defaultChecked={user.devtools}
                        />
                      </div>
                    </div>
                  </div>
                )}
                <div className="view-body-section">
                  <div className="view-body-subheader">
                    <SectionHeader
                      className="view-body-subtitle"
                      title="Appearance"
                    />
                  </div>
                  <div className="view-section-content">
                    <div className="row">
                      <Field
                        type="radio"
                        name="theme"
                        label="Choose your interface theme"
                        buttons={true}
                        options={THEME_OPTIONS}
                        className="span4 profile-theme-toggle"
                        {...(themeScope === 'local'
                          ? {
                              defaultValue: getStoredTheme(),
                              onChange: (event, value) => applyTheme(value),
                            }
                          : { defaultValue: user.theme || 'system' })}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </fieldset>
          </View>
        </Form>
        {showChangePassword && this.renderChangePassword()}
      </div>
    );
  }
}
